import { useAuth } from "@/lib/auth";
import { useActiveRole } from "@/lib/activeRole";

/**
 * Frontend RBAC helpers backed by `/auth/me` (backend is source of truth).
 * The user object carries `permissions`, `primary_role`, and `landing_route`.
 *
 * When the founder is impersonating a persona, the "effective" permission set
 * comes from the client-side mirror (rbacMatrix.js) — the UI reacts instantly
 * without a backend round-trip. The header X-Altiora-Impersonate ensures the
 * backend also uses the persona for data authorisation.
 *
 * Usage:
 *   const { has, hasAny, primaryRole, landingRoute } = usePermissions();
 *   if (has("admin.manage_members")) { ... }
 *
 *   <Can perm="founder.view_finances">
 *     <FinanceSection />
 *   </Can>
 */

export function usePermissions() {
  const { user } = useAuth();
  const { effectivePermissions, effectiveRoles, active, impersonating } = useActiveRole(user);
  const perms = effectivePermissions;
  return {
    has: (p) => perms.has(p),
    hasAny: (list) => (list || []).some((p) => perms.has(p)),
    hasAll: (list) => (list || []).every((p) => perms.has(p)),
    primaryRole: impersonating ? (effectiveRoles[0] || "user") : (user?.primary_role || "visitor"),
    landingRoute: user?.landing_route || "/app",
    roles: effectiveRoles,
    permissions: Array.from(perms),
    impersonatingAs: impersonating ? active : null,
  };
}

export function Can({ perm, anyOf, allOf, fallback = null, children }) {
  const { has, hasAny, hasAll } = usePermissions();
  let allowed = true;
  if (perm) allowed = has(perm);
  if (anyOf) allowed = allowed && hasAny(anyOf);
  if (allOf) allowed = allowed && hasAll(allOf);
  return allowed ? children : fallback;
}

// Role-based theming: subtle brand accent per role
export const ROLE_ACCENT = {
  founder:       { color: "#C5A880", label: "Fondateur",       tone: "gold" },
  super_admin:   { color: "#C5A880", label: "Super-Admin",     tone: "gold" },
  admin:         { color: "#0B2545", label: "Administration",  tone: "navy" },
  professional:  { color: "#065F46", label: "Professionnel",   tone: "emerald" },
  professionnel: { color: "#065F46", label: "Professionnel",   tone: "emerald" },
  analyst:       { color: "#7A5B14", label: "Analyste",        tone: "gold-dark" },
  analyste:      { color: "#7A5B14", label: "Analyste",        tone: "gold-dark" },
  institution:   { color: "#5B21B6", label: "Institution",     tone: "violet" },
  user:          { color: "#374151", label: "Membre",          tone: "neutral" },
  member:        { color: "#374151", label: "Membre",          tone: "neutral" },
  membre:        { color: "#374151", label: "Membre",          tone: "neutral" },
  visitor:       { color: "#8B8B8F", label: "Invité",          tone: "muted" },
};
